import { useState, useCallback, useEffect, useRef } from "react";
import { useUpdateProjectMutation } from "../app/services/projects";

const SAVED_DISPLAY_MS = 1000;
const ERROR_DISPLAY_MS = 3000;
const DEFAULT_DEBOUNCE_MS = 500;

type UpdateStatus = "idle" | "saving" | "saved" | "error";

interface UseProjectFieldSyncReturn {
  updateStatus: UpdateStatus;
  // eslint-disable-next-line no-unused-vars
  runWithStatus: <T>(task: () => Promise<T>) => Promise<T>;
  saveField: (fieldData: Record<string, unknown>) => Promise<void>;
  debouncedSave: (fieldData: Record<string, unknown>, delay?: number) => void;
  updateProject: ReturnType<typeof useUpdateProjectMutation>[0];
}

/**
 * プロジェクト設定フィールドの更新・デバウンス・ステータス管理を共通化するフック。
 *
 * - saveField: 即時 PATCH 更新（バナーフォント/カラーなど）
 * - debouncedSave: デバウンス付き PATCH（テキスト入力など）
 * - updateStatus: "idle" | "saving" | "saved" | "error"
 * - runWithStatus / updateProject: バナー画像アップロード等、PATCH の前に処理が挟まる特殊フロー用
 */
export function useProjectFieldSync(projectId: string): UseProjectFieldSyncReturn {
  const [updateProject] = useUpdateProjectMutation();
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFieldDataRef = useRef<Record<string, unknown> | null>(null);

  /** 処理の間「保存中」を出し、終わったら「保存済み」/「エラー」を一定時間出す。失敗は投げ直す */
  const runWithStatus = useCallback(async <T,>(task: () => Promise<T>): Promise<T> => {
    setUpdateStatus("saving");
    try {
      const result = await task();
      setUpdateStatus("saved");
      setTimeout(() => setUpdateStatus("idle"), SAVED_DISPLAY_MS);
      return result;
    } catch (error) {
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), ERROR_DISPLAY_MS);
      throw error;
    }
  }, []);

  const saveField = useCallback(
    async (fieldData: Record<string, unknown>) => {
      pendingFieldDataRef.current = null;
      try {
        await runWithStatus(() =>
          updateProject({ project_id: projectId, ...fieldData }).unwrap(),
        );
      } catch (error) {
        console.error("Failed to update project:", error);
      }
    },
    [runWithStatus, updateProject, projectId],
  );

  const saveFieldRef = useRef(saveField);
  saveFieldRef.current = saveField;

  const debouncedSave = useCallback(
    (fieldData: Record<string, unknown>, delay = DEFAULT_DEBOUNCE_MS) => {
      pendingFieldDataRef.current = fieldData;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => saveField(fieldData), delay);
    },
    [saveField],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        if (pendingFieldDataRef.current) {
          saveFieldRef.current(pendingFieldDataRef.current);
        }
      }
    };
  }, []);

  return { updateStatus, runWithStatus, saveField, debouncedSave, updateProject };
}
